import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import { format2 } from '@/utils/charts';
import type { AGCStorageDetailRow } from '@/types/api';

interface Props {
  data: Record<string, AGCStorageDetailRow[]>;
  height?: number;
}

function aggregateHourly(rows: AGCStorageDetailRow[]) {
  const hours: { batEnergy: number; p: number }[] = Array.from({ length: 24 }, () => ({ batEnergy: 0, p: 0 }));

  for (const r of rows) {
    const hh = parseInt(r.time.slice(0, 2), 10);
    if (Number.isNaN(hh) || hh < 0 || hh > 23) continue;
    hours[hh].batEnergy += (r.bat || 0) * (5 / 60);
  }

  const pricesByHour: Record<number, number[]> = {};
  for (const r of rows) {
    const hh = parseInt(r.time.slice(0, 2), 10);
    if (Number.isNaN(hh)) continue;
    if (!pricesByHour[hh]) pricesByHour[hh] = [];
    pricesByHour[hh].push(r.p || 0);
  }
  for (let h = 0; h < 24; h++) {
    const vals = pricesByHour[h] || [];
    hours[h].p = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
  }

  return hours;
}

const RevenueBatteryChart: React.FC<Props> = ({ data, height = 400 }) => {
  const sortedDates = useMemo(() => Object.keys(data).sort(), [data]);

  const option: EChartsOption = useMemo(() => {
    if (!sortedDates.length) return {};

    const xLabels: string[] = [];
    const batSeries: number[] = [];
    const priceSeries: number[] = [];

    for (const d of sortedDates) {
      const rows = data[d];
      if (!rows || rows.length === 0) continue;
      const mmdd = d.slice(5);
      const hourly = aggregateHourly(rows);
      for (let h = 0; h < 24; h++) {
        xLabels.push(`${mmdd} ${String(h).padStart(2, '0')}时`);
        batSeries.push(hourly[h].batEnergy);
        priceSeries.push(hourly[h].p);
      }
    }

    const batColors = batSeries.map((v) => (v < 0 ? '#22c55e' : '#ef4444'));

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          const label = params[0]?.axisValue || '';
          let html = `<strong>${label}</strong>`;
          for (const p of params) {
            if (p.seriesName === '储能电量') {
              const v = p.value as number;
              html += `<br/>${p.marker}${v < 0 ? '充电' : '放电'}: ${format2(Math.abs(v))} MWh`;
            } else if (p.seriesName === '实时电价') {
              html += `<br/>${p.marker}电价: ${format2(p.value)} 元/MWh`;
            }
          }
          return html;
        },
      },
      toolbox: { show: false },
      grid: { left: 60, right: 80, top: 30, bottom: 60 },
      xAxis: {
        type: 'category',
        data: xLabels,
        axisLabel: { fontSize: 10, rotate: 45, interval: 23 },
      },
      yAxis: (() => {
        const validPrice = priceSeries.filter((v) => Number.isFinite(v));
        const pMax = validPrice.length > 0 ? Math.max(...validPrice) : 500;
        const pCeil = Math.ceil(pMax / 100) * 100 + 100;
        return [
          { type: 'value', name: '电价 (元/MWh)', position: 'left', min: -pCeil, max: pCeil, splitLine: { lineStyle: { color: '#f0f0f0' } }, axisLine: { lineStyle: { color: '#f97316' } } },
          { type: 'value', name: '电量 (MWh)', position: 'right', min: -100, max: 100, axisLine: { show: true }, splitLine: { show: false } },
        ];
      })(),
      series: [
        {
          name: '实时电价',
          type: 'line',
          data: priceSeries,
          lineStyle: { color: '#f97316', width: 1.5 },
          itemStyle: { color: '#f97316' },
          symbol: 'none',
          yAxisIndex: 0,
        },
        {
          name: '储能电量',
          type: 'bar',
          data: batSeries.map((v, i) => ({ value: v, itemStyle: { color: batColors[i] } })),
          barWidth: '80%',
          yAxisIndex: 1,
        },
      ],
    };
  }, [data, sortedDates]);

  if (!sortedDates.length) return null;

  const first = sortedDates[0];
  const last = sortedDates[sortedDates.length - 1];
  const title = `配储分析表 (${first?.slice(5) || ''} — ${last?.slice(5) || ''})`;

  return <ChartCard title={title} option={option} height={height} />;
};

export default RevenueBatteryChart;
